import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import DocumentAttach from '../../../components/ui/DocumentAttach';
import { formatDate } from '../../../utils/formatDate';
import entityDocumentsService from '../../../services/entityDocumentsService';

/**
 * Pieces jointes d'une entite, lues dans sa table (extra_documents,
 * directive_documents) : liste (ouvrir, telecharger, renommer, supprimer) +
 * zone de depot multi-fichiers. Aucun listage Storage.
 *
 * Meme fonctionnement que DirectiveDocumentsPanel (la reference), parametre :
 *   entityType  'extra' | 'directive'
 *   entityId, projectId
 *   pdfOnly     refuse ce qui n'est pas un PDF (les directives) ; sinon tout type
 *   readOnly    consultation : ni depot, ni renommage, ni suppression
 *   onUploaded  appele avec la ligne creee, apres CHAQUE televersement reussi
 *   onCountChange(n) apres un lot ou une suppression
 *
 * `active` : le panneau est visible. A chaque activation (ou autre entite), la
 * liste repart de zero : jamais les pieces d'une autre entite.
 */

const MAX_BYTES = 20 * 1024 * 1024;
const isPdf = (file) => file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const fileIcon = (name) => {
  const ext = String(name || '')?.split('.')?.pop()?.toLowerCase();
  if (ext === 'pdf') return <Icon name="FileText" size={15} className="text-red-500 flex-shrink-0" />;
  if (['xls', 'xlsx', 'csv']?.includes(ext)) return <Icon name="FileSpreadsheet" size={15} className="text-green-600 flex-shrink-0" />;
  if (['doc', 'docx']?.includes(ext)) return <Icon name="FileText" size={15} className="text-blue-500 flex-shrink-0" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp']?.includes(ext)) return <Icon name="Image" size={15} className="text-purple-500 flex-shrink-0" />;
  return <Icon name="File" size={15} className="text-gray-400 flex-shrink-0" />;
};

const EntityDocumentsPanel = ({
  entityType,
  entityId,
  projectId,
  active = true,
  readOnly = false,
  pdfOnly = false,
  onUploaded,
  onCountChange,
}) => {
  const { t, language } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [editing, setEditing] = useState(null); // { id, value }
  const [savingName, setSavingName] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Numero de la derniere lecture lancee : une reponse plus ancienne est ignoree.
  const requestRef = useRef(0);

  // Renvoie la liste lue, ou null si une lecture plus recente l'a remplacee.
  const fetchDocs = useCallback(async () => {
    if (!entityId) return null;
    const requestId = ++requestRef.current;
    setLoading(true);
    let list = [];
    try {
      list = await entityDocumentsService.list(entityType, entityId);
    } catch {
      if (requestId === requestRef.current) setErrors([t('extras.directives.files.errorLoad')]);
    }
    if (requestId !== requestRef.current) return null;
    setDocs(list);
    setLoading(false);
    return list;
  }, [entityType, entityId, t]);

  useEffect(() => {
    requestRef.current += 1;
    setDocs([]);
    setErrors([]);
    setEditing(null);
    setConfirmDelete(null);
    setLoading(true);
    if (active && entityId) fetchDocs();
  }, [active, entityId, fetchDocs]);

  // Echap annule d'abord le renommage ou la confirmation de suppression ; la
  // modale autour ne se ferme qu'ensuite.
  useEscapeKey(() => {
    if (savingName) return;
    if (editing) setEditing(null);
    else setConfirmDelete(null);
  }, active && (!!editing || !!confirmDelete || savingName));

  const pushError = (message) => setErrors((prev) => [...prev, message]);

  // Un fichier a la fois (DocumentAttach gere la progression du lot).
  // Ne leve jamais : une erreur est affichee, le reste du lot continue.
  const handleAdd = async (file) => {
    if (pdfOnly && !isPdf(file)) {
      pushError(t('extras.directives.files.errorPdfOnly', { name: file?.name }));
      return;
    }
    if (file?.size > MAX_BYTES) {
      pushError(t('extras.directives.files.errorTooLarge', { name: file?.name }));
      return;
    }
    try {
      const row = await entityDocumentsService.upload(entityType, entityId, file, projectId);
      if (row) await onUploaded?.(row);
    } catch {
      pushError(t('extras.directives.files.errorUpload', { name: file?.name }));
    }
  };

  // Fin du lot : une seule relecture, et le compteur de la ligne suit.
  const handleComplete = async () => {
    const list = await fetchDocs();
    if (list) onCountChange?.(list?.length);
  };

  const openDoc = async (doc, download = false) => {
    try {
      const url = await entityDocumentsService.getSignedUrl(doc, download);
      if (url) window.open(url, '_blank');
      else pushError(t('documents.errorOpening'));
    } catch (err) {
      pushError(t('documents.errorCouldNotOpen', { message: err?.message }));
    }
  };

  const saveRename = async (doc) => {
    if (!editing || savingName) return;
    const value = editing?.value?.trim();
    if (!value) {
      pushError(t('extras.directives.files.errorEmptyName'));
      return;
    }
    setSavingName(true);
    try {
      const updated = await entityDocumentsService.rename(entityType, doc?.id, value, doc?.file_name);
      setDocs((prev) => prev?.map((d) => (d?.id === doc?.id ? { ...d, file_name: updated?.file_name } : d)));
      setEditing(null);
    } catch {
      pushError(t('extras.directives.files.errorRename'));
    } finally {
      setSavingName(false);
    }
  };

  const doDelete = async (doc) => {
    setConfirmDelete(null);
    setDeletingId(doc?.id);
    try {
      await entityDocumentsService.remove(entityType, doc);
      setDocs((prev) => {
        const next = prev?.filter((d) => d?.id !== doc?.id);
        onCountChange?.(next?.length);
        return next;
      });
    } catch {
      pushError(t('extras.directives.files.errorDelete'));
    } finally {
      setDeletingId(null);
    }
  };

  const iconBtn =
    'p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50';

  return (
    <div className="space-y-3">
      {/* Liste */}
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-6">
          <Icon name="Loader2" size={22} className="animate-spin text-primary" />
          <p className="text-xs text-gray-400">{t('documents.loading')}</p>
        </div>
      ) : docs?.length === 0 ? (
        <div className="text-center py-4">
          <Icon name="FileX" size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-xs text-gray-400">{t('documents.noDocumentsAttached')}</p>
        </div>
      ) : (
        <ul className="space-y-1">
          {docs?.map((doc) => {
            const isEditing = editing?.id === doc?.id;
            return (
              <li key={doc?.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50">
                {fileIcon(doc?.file_name)}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      autoFocus
                      value={editing?.value}
                      disabled={savingName}
                      onChange={(e) => setEditing({ id: doc?.id, value: e?.target?.value })}
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter') { e?.preventDefault(); saveRename(doc); }
                        if (e?.key === 'Escape') { e?.preventDefault(); setEditing(null); }
                      }}
                      aria-label={t('extras.directives.files.rename')}
                      className="w-full h-7 px-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => openDoc(doc)}
                      title={t('extras.directives.files.open')}
                      className="block w-full text-left text-xs font-medium text-gray-800 truncate hover:text-primary transition-colors"
                    >
                      {doc?.file_name}
                    </button>
                  )}
                  <p className="text-[10px] text-gray-400">
                    {[formatFileSize(doc?.size_bytes), doc?.created_at ? formatDate(doc?.created_at, language) : '']
                      ?.filter(Boolean)
                      ?.join(' · ')}
                  </p>
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button type="button" onClick={() => saveRename(doc)} disabled={savingName} className={iconBtn} title={t('extras.directives.files.renameSave')}>
                      {savingName ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Check" size={13} className="text-green-600" />}
                    </button>
                    <button type="button" onClick={() => setEditing(null)} disabled={savingName} className={iconBtn} title={t('extras.directives.files.renameCancel')}>
                      <Icon name="X" size={13} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button type="button" onClick={() => openDoc(doc, true)} className={iconBtn} title={t('extras.directives.files.download')}>
                      <Icon name="Download" size={13} />
                    </button>
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setErrors([]); setEditing({ id: doc?.id, value: doc?.file_name ?? '' }); }}
                          className={iconBtn}
                          title={t('extras.directives.files.rename')}
                        >
                          <Icon name="Pencil" size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(doc)}
                          disabled={deletingId === doc?.id}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
                          title={t('documents.deleteFileAction')}
                        >
                          {deletingId === doc?.id
                            ? <Icon name="Loader2" size={13} className="animate-spin text-red-400" />
                            : <Icon name="Trash2" size={13} />}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Erreurs */}
      {errors?.length > 0 && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
          {errors?.join('\n')}
        </div>
      )}

      {/* Depot : glisser-deposer multi-fichiers, progression par fichier */}
      {!readOnly && (
        <DocumentAttach
          documents={docs}
          showList={false}
          onAdd={handleAdd}
          onComplete={handleComplete}
          accept={pdfOnly ? 'application/pdf' : '.pdf,.xls,.xlsx,.doc,.docx,.csv,image/*'}
          hint={pdfOnly ? t('extras.directives.files.hint') : t('documents.supportedFormats')}
          disabled={loading}
        />
      )}

      {/* Confirmation de suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('documents.delete.title')}</h3>
            <p className="text-xs text-gray-500 mb-2">{t('documents.delete.message')}</p>
            <p className="text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 truncate mb-5">
              {confirmDelete?.file_name}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('documents.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                className="px-4 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                {t('documents.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityDocumentsPanel;
